import React, { useEffect, useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import images from "../../assets/images";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { ROUTES } from "../../constants/routes.constants";
import UserService from "../../services/user.service";
import Emitter from "../../utility/emitter";
import { useDispatch } from "react-redux";
import { userSlice } from "../../redux/slices/user.slice";

const decodeJwtPayload = (token) => {
  const parts = String(token).split(".");
  if (parts.length !== 3 || !parts[1]) return null;
  try {
    const base64 = parts[1].replace(/-/g, "+").replace(/_/g, "/");
    const padded = base64.padEnd(
      base64.length + ((4 - (base64.length % 4)) % 4),
      "="
    );
    return JSON.parse(atob(padded));
  } catch (error) {
    return null;
  }
};

const isResetTokenStructurallyValid = (token) => {
  if (!token) return false;
  const payload = decodeJwtPayload(token);
  if (!payload?.email || typeof payload.exp !== "number") return false;
  return payload.exp * 1000 > Date.now();
};

const isInvalidTokenResult = (result) => {
  const code = result?.error?.errorCode || result?.error?.code;
  return code === "ERR_INVALID_TOKEN";
};

const readFailureMessage = (result) => {
  const candidates = [result?.message, result?.error?.message];
  for (const candidate of candidates) {
    if (typeof candidate === "string" && candidate.trim() && candidate !== "success") {
      return candidate;
    }
  }
  return "Unable to reset your password";
};

const ResetPassword = () => {
  useEffect(() => {
    document.title = "TransferMyRx::Reset Password";
  }, []);

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [queryParameters] = useSearchParams();
  const token = queryParameters.get("token");
  const [tokenStatus, setTokenStatus] = useState(() =>
    isResetTokenStructurallyValid(token) ? "checking" : "invalid"
  );
  const [failureMessage, setFailureMessage] = useState("");

  useEffect(() => {
    let cancelled = false;
    const checkToken = async () => {
      if (!isResetTokenStructurallyValid(token)) {
        if (!cancelled) setTokenStatus("invalid");
        return;
      }

      const result = await UserService.VerifyResetPasswordToken(token);
      if (cancelled) return;
      setTokenStatus(isInvalidTokenResult(result) ? "invalid" : "ready");
    };

    checkToken();
    return () => {
      cancelled = true;
    };
  }, [token]);

  const changePasswordForm = useFormik({
    initialValues: {
      newPassword: "",
      confirmPassword: "",
    },
    validationSchema: Yup.object({
      newPassword: Yup.string()
        .required("Field is required")
        .matches(
          /^(?=.*[A-Z])(?=.*[a-z])(?=.*\d)(?=.*[!@#$%^&*()\-_=+[{\]};:<>|./?]).{8,}$/,
          "Password must contain at least 8 characters, including at least one uppercase letter, one lowercase letter, one numeric character, and one special character (!@#$%^&*)"
        ),
      confirmPassword: Yup.string()
        .required("Retype your password")
        .oneOf([Yup.ref("newPassword"), null], "Passwords must match"),
    }),
    onSubmit: async (values) => {
      const token = queryParameters.get("token");
      if (!isResetTokenStructurallyValid(token)) {
        setTokenStatus("invalid");
        return;
      }
      const action = queryParameters.get("action");
      const actionId = queryParameters.get("action-id");
      const newValues = {
        newPassword: values.newPassword,
        confirmPassword: values.confirmPassword,
        token: token,
        action,
        actionId,
      };
      const result = await UserService.ChangePassword(newValues);
      if (isInvalidTokenResult(result)) {
        setTokenStatus("invalid");
        return;
      }
      if (result.error === null) {
        Emitter.emit("SHOW_NOTIFICATION", {
          message: "Your password has been changed successfully!",
        });
        dispatch(userSlice.actions.login(result.data));
        navigate(ROUTES.LOGIN_ROUTE);
      } else {
        setFailureMessage(readFailureMessage(result));
        setTokenStatus("failed");
      }
    },
  });

  return (
    <div
      id="reset-password-form"
      className="auth-form p-4 p-sm-5 shadow-lg rounded-1 border-primary bg-white animate__animated"
    >
      <div className="text-center mb-3">
        <a href="https://www.TransferMyRx.com">
          <img src={images.Logo} alt="TransferMyRx" className="img-fluid" />
        </a>
      </div>
      {tokenStatus === "invalid" ? (
        <div className="text-center">
          <h5 className="mb-3">Invalid token</h5>
          <p className="mb-4">
            This password reset link is invalid or has expired.
          </p>
          <Link className="btn btn-default btn-lg" to={ROUTES.FORGOTPASSWORD_ROUTE}>
            Request a new link
          </Link>
        </div>
      ) : tokenStatus === "failed" ? (
        <div className="text-center">
          <h5 className="mb-3">{failureMessage}</h5>
          <p className="mb-4">
            This password reset link cannot be used.
          </p>
          <Link className="btn btn-default btn-lg" to={ROUTES.FORGOTPASSWORD_ROUTE}>
            Request a new link
          </Link>
        </div>
      ) : tokenStatus === "checking" ? (
        <h5 className="text-center mb-0">Checking your reset link...</h5>
      ) : (
        <>
          <h5 className="text-center mb-4">Reset Password</h5>
          <form onSubmit={changePasswordForm.handleSubmit}>
            <div className="col-12 mb-3">
              <label htmlFor="new-password-input" className="form-label">
                New Password
              </label>
              {changePasswordForm.touched.newPassword &&
                changePasswordForm.errors.newPassword && (
                  <small className="text-danger">
                    {changePasswordForm.errors.newPassword}
                  </small>
                )}
              <input
                id="new-password-input"
                name="newPassword"
                value={changePasswordForm.values.newPassword}
                onBlur={changePasswordForm.handleBlur}
                onChange={changePasswordForm.handleChange}
                type="password"
                placeholder="New Password"
                className="form-control"
              />
            </div>

            <div className="form-floating mb-3">
              <label htmlFor="confirm-password-input" className="form-label">
                Confirm Password
              </label>
              {changePasswordForm.touched.confirmPassword &&
                changePasswordForm.errors.confirmPassword && (
                  <small className="text-danger">
                    {changePasswordForm.errors.confirmPassword}
                  </small>
                )}
              <input
                id="confirm-password-input"
                name="confirmPassword"
                value={changePasswordForm.values.confirmPassword}
                onBlur={changePasswordForm.handleBlur}
                onChange={changePasswordForm.handleChange}
                type="password"
                placeholder="Confirm Password"
                className="form-control"
              />
            </div>

            <div className="d-grid">
              <button type="submit" className="btn btn-default btn-lg">
                Change Password
              </button>
            </div>
          </form>
        </>
      )}
    </div>
  );
};
export default ResetPassword;
